import Observables from '@happy-gastro/global-observables';
import {getAccessToken, removeAccessToken, removeLogoutReason, removeUserInfo, setLogoutReason, setUserInfo} from './storage';
import {unregisterPushNotificationsOnLogout} from '../services/pushNotificationSync';
import {getCurrentUser} from '../services/notificationApi';
import {logoutCurrentSession} from '../services/sessionsApi';
import {clearAppBadge, resetAppBadgeState} from './appBadge';
import {isRestaurantAccessRevoked, isValidUserObject, unwrapUserPayload} from './userValidation';
import {setDemoMode} from '../demo/demoState';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n, {selectableLanguages} from '../../i18n';

/**
 * Why a session was force-ended. The value is persisted (`HG_LOGOUT_REASON`) and
 * read by the Login screen to show a professional banner. Mirrors the admin
 * panel's `?error=<reason>` set (sessionGuard.js).
 *
 * @readonly
 * @enum {string}
 */
export const LOGOUT_REASON = Object.freeze({
    SESSION_EXPIRED: 'session-expired',
    ACCESS_REVOKED: 'access-revoked',
    ACCOUNT_INACTIVE: 'account-inactive',
    DEVICE_LOGGED_OUT: 'device-logged-out',
});

// Backend `code`s (on a 401) that mean the account itself is gone/disabled.
const ACCOUNT_INACTIVE_CODES = ['USER_NOT_FOUND', 'USER_DISABLED', 'USER_INACTIVE', 'ACCOUNT_DISABLED', 'ACCOUNT_INACTIVE'];

/**
 * Map an auth-failure error to a precise {@link LOGOUT_REASON}. Prefers the
 * backend's semantic `serverCode` (e.g. `DEVICE_LOGGED_OUT`, `ACCESS_REVOKED`);
 * a bare 401 with no code is treated as an expired session (the backend returns
 * a generic 401 for a deleted/disabled user, so that is the honest default).
 *
 * @param {{serverCode?: string}} error
 * @returns {string} One of {@link LOGOUT_REASON}.
 */
function classifyAuthErrorReason(error) {
    const code = error?.serverCode;

    if (code === 'DEVICE_LOGGED_OUT') {
        return LOGOUT_REASON.DEVICE_LOGGED_OUT;
    }
    if (code === 'ACCESS_REVOKED') {
        return LOGOUT_REASON.ACCESS_REVOKED;
    }
    if (ACCOUNT_INACTIVE_CODES.includes(code)) {
        return LOGOUT_REASON.ACCOUNT_INACTIVE;
    }

    return LOGOUT_REASON.SESSION_EXPIRED;
}

/**
 * Perform the app's full client-side logout flow. Identical behaviour on iOS,
 * Android and web:
 *   1. Call the backend logout endpoint (`PUT /api/user/me/logout`) while the
 *      bearer token is still present — marks the user logged out, clears the
 *      notification token and disables this device (best-effort).
 *   2. Unregister push notifications for this device.
 *   3. Clear the cached user info + access token from local storage.
 *   4. Full root remount via `restartApp` → replays the splash screen, re-runs
 *      loadRuntimeState() (re-reads the cleared token) and lands on the Login
 *      screen.
 *
 * Used by the Settings "Kijelentkezés" button, the password-change confirmation
 * flow and the "logged-in devices" screen, so the behaviour stays identical
 * everywhere.
 *
 * @param {string} [reason] One of {@link LOGOUT_REASON} when the session was
 *        force-ended (session expired / access revoked / device signed out /
 *        account inactive) — persisted so the Login screen can explain why.
 *        Omit for a deliberate user-initiated logout (no banner).
 * @returns {Promise<void>}
 */
export async function performLogout(reason) {
    // Persist WHY (or clear a stale reason for a user-initiated logout) before the
    // restartApp remount, so the Login screen can show the matching banner. Only a
    // known LOGOUT_REASON value counts — this is called both programmatically AND
    // as a bare press handler (`onPress={performLogout}`), where `reason` is the
    // gesture event, which must NOT be treated as a forced-logout reason.
    const validReason = typeof reason === 'string' && Object.values(LOGOUT_REASON).includes(reason)
        ? reason
        : null;

    try {
        if (validReason) {
            await setLogoutReason(validReason);
        } else {
            await removeLogoutReason();
        }
    } catch (_error) {
        // Non-fatal — the banner is a nicety, never block logout on it.
    }

    // Driver GPS tracking must end with the session: one last upload while the
    // token is still valid, then the background task and the buffer are dropped.
    // Required lazily so non-driver builds / tests never load expo-location here.
    try {
        const {stopTrackingOnLogout} = require('./driverTracking');

        await stopTrackingOnLogout();
    } catch (_error) {
        // Non-fatal — never block logout on tracking cleanup.
    }

    // Work-event location pings: drop the offline queue + config cache so the next user never inherits them.
    try {
        const {clearWorkLocationData} = require('./workLocation');

        await clearWorkLocationData();
    } catch (_error) {
        // Non-fatal — never block logout on location cleanup.
    }

    // Server-side logout FIRST, while the bearer token still exists — clears the
    // notification token and disables this device. Best-effort: a network/backend
    // failure must never trap the user in a signed-in state.
    try {
        await logoutCurrentSession();
    } catch (_error) {
        // Non-fatal — proceed with the local logout regardless.
    }
    try {
        await unregisterPushNotificationsOnLogout();
    } catch (_error) {
        // Non-fatal — never block logout on a push-unregister failure.
    }
    // Wipe the app icon badge (and its cached state) — the next user must never
    // inherit the previous session's unread notification/mail counters.
    try {
        await clearAppBadge();
        resetAppBadgeState();
    } catch (_error) {
        // Non-fatal — a stale badge is cosmetic, never block logout on it.
    }
    // Home-screen widgets: swap the previous user's data for the signed-out card (and drop the iOS App Group token) so a
    // running clock / private numbers never outlive the session.
    try {
        const {clearWidgetData} = require('./widgets/widgetBridge');

        await clearWidgetData();
    } catch (_error) {
        // Non-fatal — never block logout on widget cleanup.
    }
    await removeUserInfo();
    await removeAccessToken();
    // Demo mode is session-only — force it off so a logged-out / next user never
    // inherits the previous session's presentation mode.
    try {
        await setDemoMode(false);
    } catch (_error) {
        // Non-fatal — never block logout on the demo-flag reset.
    }
    // Full <App/> root remount (index.js `Root`) on EVERY platform: replays the
    // splash screen, then loadRuntimeState() re-reads the now-cleared token →
    // isLoggedIn=false → AppNavigator lands on StartScreen → Login. This is a
    // React remount (not an OS process restart), so unlike the old
    // reloadAppMode/BackHandler.exitApp path it reliably returns BOTH iOS and
    // Android to the sign-in screen and tears down every provider mounted above
    // the navigator (no previous-user data leaks into the next session).
    Observables.call('restartApp');
}

/**
 * Validate the current session against the backend and force a full logout if it
 * is no longer valid.
 *
 * Calls `GET /api/user/me`. If the backend returns something that is NOT a valid
 * user object (stale/revoked token → error body, HTML, empty payload, or a 401/
 * 403), the session is dead → run {@link performLogout} (clears token + user,
 * then soft-reloads back to the Login screen).
 *
 * Network/transport failures (offline, timeout, 5xx) are deliberately ignored so
 * the user is NOT kicked out just because they momentarily lost connectivity —
 * the cached session stays put and the next call retries.
 *
 * Safe to call non-blocking after boot; never throws.
 *
 * @returns {Promise<boolean>} True if the session is valid, false if logged out.
 */
export async function validateSessionOrLogout() {
    // No token → nothing to validate (the navigator already shows Login).
    const accessToken = await getAccessToken();
    if (!accessToken) {
        return false;
    }

    let user;
    try {
        user = unwrapUserPayload(await getCurrentUser());
    } catch (error) {
        // Only a definitive auth rejection (401/403) means "log out". Anything
        // else (offline, 5xx, parse error) keeps the cached session intact. The
        // reason is derived from the backend code (device signed out / access
        // revoked / account inactive), defaulting to an expired session.
        if (error?.code === 'AUTH_REQUIRED' || error?.status === 401 || error?.status === 403) {
            await performLogout(classifyAuthErrorReason(error));
            return false;
        }
        return true;
    }

    // Token still valid but /me is not a real user (deleted/disabled account).
    if (!isValidUserObject(user)) {
        await performLogout(LOGOUT_REASON.ACCOUNT_INACTIVE);
        return false;
    }

    // Still-valid session whose restaurant access was revoked mid-session — a 200
    // the backend cannot signal as a 401 (the access gate is login-time only).
    if (isRestaurantAccessRevoked(user)) {
        await performLogout(LOGOUT_REASON.ACCESS_REVOKED);
        return false;
    }

    // Valid session — keep the locally cached user fresh for offline screens.
    try {
        await setUserInfo(user);
    } catch (_error) {
        // Non-fatal: a cache write failure must not log the user out.
    }

    // Adopt the language saved on the user's backend profile so the UI matches the
    // language notifications / SMS / e-mail are sent in — and so a chosen language
    // survives a reinstall or a login on a new device. Only for supported UI locales.
    try {
        const profileLang = typeof user?.language === 'string' ? user.language : '';
        if (profileLang && selectableLanguages.includes(profileLang)) {
            const current = await AsyncStorage.getItem('selectedLanguage');
            if (current !== profileLang) {
                await AsyncStorage.setItem('selectedLanguage', profileLang);
                await i18n.changeLanguage(profileLang);
            }
        }
    } catch (_error) {
        // Non-fatal: language sync must never affect session validity.
    }

    return true;
}

export default performLogout;
